import { Description } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Title from 'lib/designSystem/Title';
import { SignatureType } from 'lib/types/models';
import React from 'react';

export interface PendingDocument {
  file: File;
  description: string;
  signature: SignatureType;
}

interface DocumentsDropzoneProps {
  documents: PendingDocument[];
  onChange: (docs: PendingDocument[]) => void;
  disabled?: boolean;
}

const DocumentsDropzone: React.FC<DocumentsDropzoneProps> = ({
  documents,
  onChange,
  disabled,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const addFiles = (files: FileList | File[]) => {
    const filesArray = Array.from(files);
    const newDocs: PendingDocument[] = filesArray.map((file) => ({
      file,
      description: file.name.replace(/\.[^/.]+$/, ''),
      signature: SignatureType.FES,
    }));
    onChange([...(documents ?? []), ...newDocs]);
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (index: number) => {
    const next = [...documents];
    next.splice(index, 1);
    onChange(next);
  };

  const updateDoc = (index: number, patch: Partial<PendingDocument>) => {
    const next = [...documents];
    next[index] = { ...next[index], ...patch } as PendingDocument;
    onChange(next);
  };

  return (
    <Box
      sx={{
        backgroundColor: 'grey.50',
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Title
          title="Documentos a firmar"
          icon={<Description color="primary" sx={{ fontSize: 16 }} />}
          iconSize={30}
          titleSize="0.875rem"
          color="primary"
        />
        {documents && documents.length !== 0 && (
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => {
              if (!disabled) inputRef.current?.click();
            }}
          >
            <AddCircleOutlineIcon />
          </IconButton>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        multiple
        hidden
        onChange={handleBrowse}
        disabled={disabled}
      />

      {!documents ||
        (documents.length === 0 && (
          <Box
            sx={{
              border: '2px dashed #B0B8C4',
              borderRadius: 2,
              textAlign: 'center',
              padding: 6,
              cursor: 'pointer',
              background: '#FAFBFC',
              mb: 2,
            }}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDrop={handleDrop}
            onClick={() => {
              if (!disabled) inputRef.current?.click();
            }}
          >
            <CloudUploadIcon color="primary" />
            <Typography variant="body1" mt={1}>
              Arrastra y suelta archivos PDF aquí, o haz clic para buscar
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Solo se permiten archivos PDF.
            </Typography>
          </Box>
        ))}

      {documents?.length > 0 && (
        <Grid container spacing={1.5}>
          {documents.map((doc, idx) => (
            <Grid item xs={12} key={`${doc.file.name}-${idx}`}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  borderColor: '#E5E7EB',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        backgroundColor: 'rgba(220, 38, 38, 0.08)',
                        borderRadius: '6px',
                        p: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PictureAsPdfIcon
                        sx={{ fontSize: 18, color: 'error.main' }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle2"
                      noWrap
                      title={doc.file.name}
                      sx={{
                        fontWeight: 600,
                        maxWidth: { xs: 120 },
                      }}
                    >
                      {doc.file.name}
                    </Typography>
                    <IconButton
                      aria-label="remove"
                      onClick={() => removeAt(idx)}
                      disabled={disabled}
                      size="small"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id={`signature-label-${idx}`}>
                        Tipo de firma
                      </InputLabel>
                      <Select
                        labelId={`signature-label-${idx}`}
                        label="Tipo de firma"
                        value={doc.signature}
                        onChange={(e) =>
                          updateDoc(idx, {
                            signature: e.target.value as SignatureType,
                          })
                        }
                        disabled={disabled}
                      >
                        <MenuItem value={SignatureType.FEA}>FEA</MenuItem>
                        <MenuItem value={SignatureType.FES}>FES</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <TextField
                    placeholder="Descripción"
                    value={doc.description}
                    onChange={(e) =>
                      updateDoc(idx, { description: e.target.value })
                    }
                    disabled={disabled}
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default DocumentsDropzone;
